import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Flame, PackageCheck, ShoppingBasket, SlidersHorizontal, Sparkles, Star, Truck, Warehouse, X } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard, ProductSkeleton } from "@/components/ProductCard";
import { QrSection } from "@/components/QrSection";
import { fmtGNF } from "@/lib/format";
import { DealCard } from "@/components/deals";

const HERO_IMG =
  "https://images.pexels.com/photos/29786116/pexels-photo-29786116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Section = ({ title, subtitle, icon: Icon, to, children }) => (
  <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
    <div className="mb-6 flex items-end justify-between gap-3">
      <div>
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          {Icon && <Icon className="h-5 w-5 text-gold" />} {title}
        </h2>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {to && (
        <Link
          to={to}
          className="group flex shrink-0 items-center gap-1 text-sm font-semibold text-brand transition-colors hover:text-emerald-900"
        >
          Voir tout <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      )}
    </div>
    {children}
  </section>
);

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);
  const [recent, setRecent] = useState([]);
  const [promos, setPromos] = useState([]);
  const [deals, setDeals] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    (async () => {
      try {
        const [c, p, r, pr, z, dl] = await Promise.all([
          api.get("/categories"),
          api.get("/products", { params: { sort: "popular", limit: 10 } }),
          api.get("/products", { params: { sort: "recent", limit: 10 } }),
          api.get("/products", { params: { promo: true, limit: 8 } }),
          api.get("/delivery-zones"),
          api.get("/products", { params: { deal: true, sort: "deal_ending", limit: 10 } }),
        ]);
        setCategories(c.data);
        setPopular(p.data.items);
        setRecent(r.data.items);
        setPromos(pr.data.items);
        setZones(z.data);
        setDeals(dl.data.items);
        setFeatured((await api.get("/products", { params: { featured: true, limit: 3 } })).data.items);
      } catch {
        toast.error("Impossible de charger la boutique. Vérifiez votre connexion.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const add = (product) => {
    addItem(product, 1);
    toast.success(`${product.name} ajouté au panier`);
  };

  const skeletons = Array.from({ length: 5 });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-4 pt-5 sm:px-6">
        <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
          <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-emerald-900 via-brand to-slate-900 text-white shadow-[0_28px_60px_-32px_rgba(6,78,59,0.75)] ds-rise">
            <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/70 via-transparent to-transparent" />
            <div className="relative p-6 sm:p-10 lg:p-12">
              <span className="ds-chip border-white/25 bg-white/10 text-white backdrop-blur">
                <Warehouse className="h-3.5 w-3.5" /> Entrepôt 300 m² · Guinée
              </span>
              <h1 className="mt-5 font-display text-3xl font-extrabold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
                Détail et gros,<br />
                <span className="text-gold">livrés partout en Guinée</span>
              </h1>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-emerald-50/90 sm:text-base">
                Prix de gros et vente au détail. <strong className="font-semibold text-gold">Retrait à l'entrepôt gratuit</strong> —
                ou livraison pour 10 000 GNF, offerte dès 100 000 GNF. Paiement avant livraison.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/catalogue" className="ds-btn-primary bg-gold hover:bg-gold-dark" data-testid="hero-catalog-cta">
                  Voir le catalogue <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/offres"
                  className="ds-btn-ghost border-white/40 bg-white/10 text-white hover:bg-white/20"
                  data-testid="hero-promo-cta"
                >
                  Offres du jour
                </Link>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
            <div className="ds-card flex items-start gap-3 p-5">
              <Truck className="h-8 w-8 shrink-0 text-brand" />
              <div>
                <p className="font-display text-sm font-bold text-slate-900">Livraison Entrepôt 10 000 GNF</p>
                <p className="mt-1 text-xs text-slate-500">
                  {zones.slice(0, 3).map((z) => `${z.name} ${z.fee > 0 ? fmtGNF(z.fee) : "Gratuit"}`).join(" · ") || "Zones configurables"}
                </p>
              </div>
            </div>
            <div className="ds-card flex items-start gap-3 p-5">
              <PackageCheck className="h-8 w-8 shrink-0 text-gold" />
              <div>
                <p className="font-display text-sm font-bold text-slate-900">Paiement avant livraison</p>
                <p className="mt-1 text-xs text-slate-500">
                  Orange Money, MTN Mobile Money ou virement. Le colis part après confirmation de l'encaissement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {deals.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pt-8 sm:px-6" data-testid="home-deals">
          <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-rose-700 via-rose-600 to-amber-600 p-5 shadow-[0_20px_50px_-28px_rgba(190,18,60,0.65)] sm:p-7">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 text-white">
                <Flame className="h-7 w-7 shrink-0 text-amber-200" />
                <div>
                  <h2 className="font-display text-xl font-extrabold tracking-tight sm:text-2xl">Offres du jour</h2>
                  <p className="text-xs text-rose-50/90 sm:text-sm">Prix cassés — chaque offre expire bientôt</p>
                </div>
              </div>
              <Link
                to="/offres"
                className="shrink-0 rounded-xl bg-white/15 px-3 py-2 text-xs font-semibold text-white backdrop-blur transition-colors hover:bg-white/25"
                data-testid="home-deals-see-all"
              >
                Voir tout
              </Link>
            </div>
            <div className="no-scrollbar mt-5 flex gap-3 overflow-x-auto pb-1" data-testid="home-deals-carousel">
              {deals.map((p) => (
                <div key={p.id} className="w-40 shrink-0 sm:w-52">
                  <DealCard product={p} onAdd={add} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {featured.length > 0 && (
        <Section title="Les articles du moment" subtitle="Notre sélection à ne pas manquer" icon={Star} to="/catalogue">
          <div className="grid gap-3 sm:grid-cols-3 sm:gap-5" data-testid="featured-section">
            {featured.slice(0, 3).map((p) => (
              <article key={p.id} className="ds-card group flex gap-3 overflow-hidden p-3" data-testid={`featured-${p.id}`}>
                <Link to={`/produit/${p.id}`} className="relative block h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-slate-100 sm:h-28 sm:w-28">
                  {(p.images || [])[0] && (
                    <img
                      src={mediaUrl(p.images[0])}
                      alt={p.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                </Link>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="ds-chip w-fit border-gold bg-gold-light text-gold-dark">Vedette</span>
                  <Link to={`/produit/${p.id}`} className="mt-1 line-clamp-2 text-sm font-semibold leading-snug text-slate-900 hover:text-brand">
                    {p.name}
                  </Link>
                  <p className="font-num mt-1 text-base font-bold text-brand">{fmtGNF(p.price)}</p>
                  {p.compare_price > p.price && (
                    <p className="font-num text-xs text-slate-400 line-through">{fmtGNF(p.compare_price)}</p>
                  )}
                  <button
                    type="button"
                    disabled={(p.stock || 0) <= 0}
                    onClick={() => add(p)}
                    className="ds-btn-primary mt-auto w-full text-xs"
                    data-testid={`featured-add-${p.id}`}
                  >
                    <ShoppingBasket className="h-4 w-4" /> Ajouter
                  </button>
                </div>
              </article>
            ))}
          </div>
        </Section>
      )}

      {categories.some((c) => c.product_count > 0) && (
      <section className="mx-auto max-w-7xl px-4 pt-8 sm:px-6">
        <h2 className="mb-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Catégories</h2>
        <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-1" data-testid="category-strip">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/catalogue?category=${c.id}`}
              className="ds-card shrink-0 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-brand hover:text-brand"
              data-testid={`category-pill-${c.slug}`}
            >
              {c.name}
              <span className="font-num ml-2 text-xs text-slate-400">{c.product_count}</span>
            </Link>
          ))}
        </div>
      </section>
      )}

      {!loading && popular.length === 0 && recent.length === 0 && (
        <section className="ds-card mt-6 p-8 text-center" data-testid="home-empty-state">
          <Sparkles className="mx-auto h-10 w-10 text-gold" />
          <p className="mt-3 font-display text-lg font-bold text-slate-900">Le catalogue arrive bientôt</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
            Nos équipes préparent les premiers articles. Livraison depuis l'entrepôt pour 10 000 GNF,
            offerte dès 100 000 GNF, et paiement avant livraison par Orange Money, MTN ou virement.
          </p>
          <Link to="/professionnels" className="ds-btn-primary mt-5" data-testid="home-empty-pro-cta">
            Vous êtes vendeur ? Rejoignez la marketplace
          </Link>
        </section>
      )}

      {(loading || popular.length > 0) && (
        <Section title="Produits populaires" subtitle="Les plus demandés en Guinée" icon={Flame} to="/catalogue?sort=popular">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 sm:gap-5">
            {loading
              ? skeletons.map((_, i) => <ProductSkeleton key={i} />)
              : popular.slice(0, 10).map((p) => <ProductCard key={p.id} product={p} onAdd={add} />)}
          </div>
        </Section>
      )}

      {promos.length > 0 && (
        <Section title="Promotions" subtitle="Prix réduits, quantités limitées" icon={Sparkles} to="/catalogue?promo=1">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 sm:gap-5">
            {promos.slice(0, 5).map((p) => (
              <ProductCard key={p.id} product={p} onAdd={add} />
            ))}
          </div>
        </Section>
      )}

      {(loading || recent.length > 0) && (
        <Section title="Nouveautés" subtitle="Derniers arrivages à l'entrepôt" to="/catalogue">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 sm:gap-5">
            {loading
              ? skeletons.map((_, i) => <ProductSkeleton key={i} />)
              : recent.slice(0, 10).map((p) => <ProductCard key={p.id} product={p} onAdd={add} />)}
          </div>
        </Section>
      )}

      <QrSection />
    </div>
  );
}

export function Catalog() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { addItem } = useCart();

  const search = params.get("search") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "recent";
  const promo = params.get("promo") || "";
  const wholesale = params.get("wholesale") || "";
  const inStock = params.get("in_stock") || "";
  const priceMin = params.get("price_min") || "";
  const priceMax = params.get("price_max") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", {
        params: {
          search: search || undefined,
          category_id: category || undefined,
          sort,
          promo: promo ? true : undefined,
          wholesale: wholesale ? true : undefined,
          in_stock: inStock ? true : undefined,
          price_min: priceMin || undefined,
          price_max: priceMax || undefined,
          limit: 60,
        },
      })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erreur de chargement du catalogue"))
      .finally(() => setLoading(false));
  }, [search, category, sort, promo, wholesale, inStock, priceMin, priceMax]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const activeCategory = useMemo(() => categories.find((c) => c.id === category), [categories, category]);

  const PRICE_RANGES = [
    { label: "Moins de 100 000 GNF", min: "", max: "100000" },
    { label: "100 000 – 500 000 GNF", min: "100000", max: "500000" },
    { label: "500 000 – 2 000 000 GNF", min: "500000", max: "2000000" },
    { label: "Plus de 2 000 000 GNF", min: "2000000", max: "" },
  ];

  const resetFilters = () => {
    const next = new URLSearchParams(params);
    ["category", "promo", "wholesale", "in_stock", "price_min", "price_max"].forEach((k) => next.delete(k));
    setParams(next);
  };

  const Filters = () => (
    <div className="space-y-5" data-testid="catalog-filters">
      <div>
        <p className="ds-label">Rayons</p>
        <ul className="mt-2 space-y-1 text-sm">
          <li>
            <button
              type="button"
              onClick={() => update("category", "")}
              className={`text-left transition-colors hover:text-brand ${!category ? "font-bold text-brand" : "text-slate-600"}`}
              data-testid="filter-all"
            >
              Tous les rayons
            </button>
          </li>
          {categories.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => update("category", c.id)}
                className={`text-left transition-colors hover:text-brand ${category === c.id ? "font-bold text-brand" : "text-slate-600"}`}
                data-testid={`filter-category-${c.slug}`}
              >
                {c.name} <span className="font-num text-xs text-slate-400">{c.product_count}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <p className="ds-label">Prix</p>
        <ul className="mt-2 space-y-1 text-sm">
          {PRICE_RANGES.map((r) => {
            const active = priceMin === r.min && priceMax === r.max;
            return (
              <li key={r.label}>
                <button
                  type="button"
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    if (active) {
                      next.delete("price_min");
                      next.delete("price_max");
                    } else {
                      r.min ? next.set("price_min", r.min) : next.delete("price_min");
                      r.max ? next.set("price_max", r.max) : next.delete("price_max");
                    }
                    setParams(next);
                  }}
                  className={`text-left transition-colors hover:text-brand ${active ? "font-bold text-brand" : "text-slate-600"}`}
                  data-testid={`filter-price-${r.min || "0"}-${r.max || "max"}`}
                >
                  {r.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div>
        <p className="ds-label">Affiner</p>
        <div className="mt-2 space-y-2 text-sm text-slate-600">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(promo)}
              onChange={(e) => update("promo", e.target.checked ? "1" : "")}
              data-testid="filter-promo"
            />
            En promotion
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(wholesale)}
              onChange={(e) => update("wholesale", e.target.checked ? "1" : "")}
              data-testid="filter-wholesale"
            />
            Prix de gros disponible
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(inStock)}
              onChange={(e) => update("in_stock", e.target.checked ? "1" : "")}
              data-testid="filter-in-stock"
            />
            En stock uniquement
          </label>
        </div>
      </div>

      <button type="button" onClick={resetFilters} className="text-xs font-semibold text-brand underline" data-testid="filter-reset">
        Réinitialiser les filtres
      </button>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6" data-testid="catalog-page">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-slate-500" data-testid="catalog-breadcrumb">
        <Link to="/" className="hover:text-brand">Accueil</Link>
        <span>›</span>
        <Link to="/catalogue" className="hover:text-brand">Catalogue</Link>
        {activeCategory && (
          <>
            <span>›</span>
            <span className="font-semibold text-slate-700">{activeCategory.name}</span>
          </>
        )}
      </nav>

      <h1 className="ds-h1 mt-2">
        {search ? `Résultats pour « ${search} »` : activeCategory ? activeCategory.name : promo ? "Promotions" : "Catalogue"}
      </h1>

      <div className="mt-5 grid gap-6 lg:grid-cols-[230px_1fr]">
        <aside className="hidden lg:block">
          <Filters />
        </aside>

        <div>
          <div className="ds-card flex flex-wrap items-center justify-between gap-3 p-3">
            <p className="text-sm text-slate-600" data-testid="catalog-count">
              {loading ? "Chargement…" : `${data.total} résultat(s)`}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="ds-btn-ghost py-2 text-xs lg:hidden"
                data-testid="catalog-open-filters"
              >
                <SlidersHorizontal className="h-4 w-4" /> Filtres
              </button>
              <label className="ds-label hidden sm:block">Trier par</label>
              <select
                value={sort}
                onChange={(e) => update("sort", e.target.value)}
                className="ds-input max-w-[200px] py-2"
                data-testid="catalog-sort-select"
              >
                <option value="recent">Nouveautés</option>
                <option value="popular">Popularité</option>
                <option value="price_asc">Prix croissant</option>
                <option value="price_desc">Prix décroissant</option>
              </select>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-5">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => <ProductSkeleton key={i} />)
              : data.items.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onAdd={(prod) => {
                      addItem(prod, 1);
                      toast.success(`${prod.name} ajouté au panier`);
                    }}
                  />
                ))}
          </div>

          {!loading && data.items.length === 0 && (
            <div className="ds-card mt-6 p-10 text-center" data-testid="catalog-empty">
              <p className="font-display text-lg font-semibold text-slate-900">Aucun produit trouvé</p>
              <p className="mt-1 text-sm text-slate-500">Essayez un autre mot-clé, une autre catégorie ou retirez des filtres.</p>
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden" data-testid="catalog-filters-drawer">
          <div className="flex-1 bg-slate-900/50" onClick={() => setFiltersOpen(false)} />
          <div className="w-72 overflow-y-auto bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-display text-base font-bold text-slate-900">Filtres</p>
              <button type="button" onClick={() => setFiltersOpen(false)} data-testid="catalog-close-filters">
                <X className="h-5 w-5 text-slate-400" />
              </button>
            </div>
            <Filters />
          </div>
        </div>
      )}
    </div>
  );
}
